<template>
  <div class="min-h-screen bg-linear-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 relative ">
    <BreadCrumbs :sessionId="sessionId" />
    <PhaseNavigation />
    
    <!-- Action Bar -->
    <div class="sticky top-16 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800 shadow-sm">
      <div class="max-w-7xl mx-auto px-6 py-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <!-- Report Status Indicator -->
            <div class="flex items-center gap-2 px-3 py-1.5 rounded-lg" :class="reportStatusColor">
              <div class="w-2 h-2 rounded-full" :class="reportStatusDotColor"></div>
              <span class="text-sm font-medium">{{ reportStatusText }}</span>
            </div>
            
            <template v-if="riskReport">
              <span class="text-gray-400">•</span>
              <span class="text-sm text-gray-600 dark:text-gray-400">{{ riskReport.sections?.length || 0 }} Sections</span>
              <span class="text-gray-400">•</span>
              <span class="text-sm text-gray-600 dark:text-gray-400">{{ riskReport.footnotes?.length || 0 }} Citations</span>
            </template>
          </div>
          
          <div class="flex items-center gap-3">
            <!-- Generate Report -->
            <button
              v-if="!riskReport"
              @click="generateReport"
              :disabled="isLoading"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <template v-if="isLoading">
                <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Generating Report...
              </template>
              <template v-else>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Generate Report
              </template>
            </button>

            <!-- Regenerate Report (when report exists) -->
            <button
              v-if="riskReport"
              @click="regenerateReport"
              :disabled="isLoading"
              class="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <template v-if="isLoading">
                <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Regenerating...
              </template>
              <template v-else>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Regenerate Report
              </template>
            </button>

            <!-- Export Options -->
            <button
              v-if="riskReport"
              @click="showExportMenu = !showExportMenu"
              :disabled="isLoading"
              class="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>

            <!-- Export Menu (when export is available) -->
            <div v-if="riskReport && !isLoading && showExportMenu" class="relative">
              <div v-if="showExportMenu" class="absolute right-0 top-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-2 min-w-[200px] z-20">
                  <button
                    @click="handleExport('pdf')"
                    :disabled="isExporting"
                    class="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
                  >
                    <svg class="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                    </svg>
                    <span>Export as PDF</span>
                  </button>
                  <button
                    @click="handleExport('docx')"
                    :disabled="isExporting"
                    class="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
                  >
                    <svg class="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                    </svg>
                    <span>Export as Word</span>
                  </button>
                  <button
                    @click="handleExport('txt')"
                    :disabled="isExporting"
                    class="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
                  >
                    <svg class="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                    </svg>
                    <span>Export as Text</span>
                  </button>
                  <hr class="my-1.5 border-gray-200 dark:border-gray-600">
                  <button
                    @click="handleExport('copy')"
                    :disabled="isExporting"
                    class="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 transition-colors"
                  >
                    <svg class="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z" />
                      <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5z" />
                    </svg>
                    <span>Copy to Clipboard</span>
                  </button>
              </div>
            </div>
          </div>
        
          <!-- Report Type Selection - Hidden for now -->
          <!-- 
          <div>
            <div class="flex items-center gap-3">
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Report Type:</span>
              <select 
                v-model="selectedReportType" 
                @change="handleReportTypeChange"
                :disabled="isLoading"
                class="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 min-w-[180px]"
              >
                <option 
                  v-for="option in reportTypeOptions" 
                  :key="option.value" 
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
          -->
        </div>
      </div>
    </div>

    <main class="flex-1 w-full pb-32">
      <section class="w-full max-w-7xl mx-auto px-6 py-8">

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-16">
          <div class="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-6"></div>
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">Generating Risk Report</h3>
          <p class="text-gray-600 dark:text-gray-400 mb-4">
            Creating comprehensive due diligence report with citations...
          </p>
          <div class="text-sm text-gray-500 dark:text-gray-500 max-w-md mx-auto">
            <p>This process typically takes 1-3 minutes depending on the amount of data to analyze.</p>
          </div>
        </div>

        <!-- Error State -->
        <div v-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center mb-8">
          <div class="text-red-600 dark:text-red-400 mb-2">
            <svg class="w-12 h-12 mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <h3 class="font-semibold text-red-900 dark:text-red-100 mb-2">Report Generation Error</h3>
          <p class="text-red-700 dark:text-red-300 mb-4">{{ error }}</p>
          <button @click="clearError" class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">
            Dismiss
          </button>
        </div>

        <!-- No Report State -->
        <div v-if="!riskReport && !isLoading && !error" class="text-center py-16">
          <div class="text-gray-400 mb-6">
            <svg class="w-20 h-20 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
            </svg>
          </div>
          <h3 class="text-2xl font-semibold text-gray-900 dark:text-white mb-3">No Risk Report Available</h3>
          <p class="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            Generate a comprehensive due diligence report with proper citations and source verification.
          </p>
          <div class="space-y-4">
            <button
              @click="generateReport"
              class="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-lg"
            >
              Generate Risk Report
            </button>
            
            <!-- Report Type Selection -->
            <div class="flex items-center justify-center gap-4">
              <label class="text-sm text-gray-600 dark:text-gray-400">Report Type:</label>
              <select
                v-model="selectedReportType"
                class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option 
                  v-for="option in reportTypeOptions" 
                  :key="option.value" 
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Report Display -->
        <div v-if="riskReport && !isLoading">
          <ReportDisplay :report="riskReport" />
          
          <!-- Citation Verification Section -->
          <div v-if="verificationResults" class="mt-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Citation Verification Results</h3>
            
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div class="text-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div class="text-2xl font-bold text-gray-900 dark:text-white">
                  {{ verificationResults.summary?.verificationRate || '0%' }}
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">Verification Rate</div>
              </div>
              
              <div class="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div class="text-2xl font-bold text-green-600 dark:text-green-400">
                  {{ verificationResults.summary?.verifiedCount || 0 }}
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">Verified Citations</div>
              </div>
              
              <div class="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div class="text-2xl font-bold text-red-600 dark:text-red-400">
                  {{ verificationResults.summary?.brokenLinks || 0 }}
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">Broken Links</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
          {{ successMessage }}
        </div>
      </section>
    </main>

    <!-- Floating Agent Button -->
    <button
      @click="isAgentOpen = !isAgentOpen"
      class="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-50 flex items-center justify-center group"
      :class="{ 'scale-95': isAgentOpen }"
    >
      <!-- Close icon when open -->
      <svg v-if="isAgentOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
      <!-- AI Chat icon when closed -->
      <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    </button>

    <!-- Agent Chat Overlay Panel -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <div
        v-if="isAgentOpen"
        ref="agentPanelRef"
        class="fixed bottom-24 right-6 w-[400px] max-h-[600px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 flex flex-col overflow-hidden"
      >
        <AgentChatSidebar
          :activeProject="session"
          :loading="isLoading"
          @close="isAgentOpen = false"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BreadCrumbs from '@/layouts/BreadCrumbs.vue'
import PhaseNavigation from '@/components/PhaseNavigation.vue'
import AgentChatSidebar from '@/components/AgentChatSidebar.vue'
import ReportDisplay from '@/components/ReportDisplay.vue'
import { useRiskReportStore } from '@/stores/riskReport'
import { exportReport } from '@/utils/reportExports'

const route = useRoute()
const router = useRouter()
const riskReportStore = useRiskReportStore()

// Route parameters as computed properties
const projectId = computed(() => route.query.projectId)
const sessionId = computed(() => route.query.sessionId)

// Agent chat overlay state
const isAgentOpen = ref(false)
const agentPanelRef = ref(null)

// Session context for agent
const session = computed(() => ({
  _id: sessionId.value,
  projectId: projectId.value,
  phase: 'reporting',
  riskReport: riskReport.value
}))

// State
const isLoading = ref(false)
const isExporting = ref(false)
const error = ref(null)
const showExportMenu = ref(false)
const selectedReportType = ref('comprehensive')
const successMessage = ref(null)

// Computed properties
const riskReport = computed(() => riskReportStore.riskReport)
const reportStatus = computed(() => riskReportStore.reportStatus)
const verificationResults = computed(() => riskReportStore.verificationResults)

// Report type options
const reportTypeOptions = computed(() => riskReportStore.reportTypeOptions)

// Report status computed properties
const reportStatusText = computed(() => {
  if (!riskReport.value && !reportStatus.value) return 'No Report'
  
  if (reportStatus.value?.status) {
    switch (reportStatus.value.status) {
      case 'completed':
        return 'Report Complete'
      case 'generating':
        return 'Generating Report'
      case 'under_review':
        return 'Under Review'
      case 'approved':
        return 'Report Approved'
      default:
        return 'Report Ready'
    }
  }
  
  return 'Report Available'
})

const reportStatusColor = computed(() => {
  if (!riskReport.value && !reportStatus.value) return 'bg-gray-50 dark:bg-gray-800'
  
  if (reportStatus.value?.status) {
    switch (reportStatus.value.status) {
      case 'completed':
      case 'approved':
        return 'bg-green-50 dark:bg-green-900/30'
      case 'generating':
        return 'bg-amber-50 dark:bg-amber-900/30'
      case 'under_review':
        return 'bg-blue-50 dark:bg-blue-900/30'
      default:
        return 'bg-gray-50 dark:bg-gray-800'
    }
  }
  
  return 'bg-green-50 dark:bg-green-900/30'
})

const reportStatusDotColor = computed(() => {
  if (!riskReport.value && !reportStatus.value) return 'bg-gray-400'
  
  if (reportStatus.value?.status) {
    switch (reportStatus.value.status) {
      case 'completed':
      case 'approved':
        return 'bg-green-500'
      case 'generating':
        return 'bg-amber-500'
      case 'under_review':
        return 'bg-blue-500'
      default:
        return 'bg-gray-400'
    }
  }
  
  return 'bg-green-500'
})

// Methods
const generateReport = async () => {
  isLoading.value = true
  error.value = null
  
  try {
    if (!sessionId.value) {
      throw new Error('Session ID is required for report generation')
    }
    
    await riskReportStore.generateRiskReport(projectId.value, sessionId.value, {
      reportType: selectedReportType.value
    })
    
    showSuccessMessage('Risk report generated successfully!')
  } catch (err) {
    error.value = err.message || 'Failed to generate risk report'
  } finally {
    isLoading.value = false
  }
}

const regenerateReport = async () => {
  if (isLoading.value) return // Prevent multiple calls
  
  isLoading.value = true
  error.value = null
  
  try {
    if (!sessionId.value) {
      throw new Error('Session ID is required for report regeneration')
    }
    
    await riskReportStore.regenerateRiskReport(projectId.value, sessionId.value, {
      reportType: selectedReportType.value
    })
    
    showSuccessMessage('Risk report regenerated successfully!')
  } catch (err) {
    error.value = err.message || 'Failed to regenerate risk report'
  } finally {
    isLoading.value = false
  }
}

const handleExport = async (format) => {
  if (!riskReport.value) return
  
  isExporting.value = true
  showExportMenu.value = false
  
  try {
    await exportReport(riskReport.value, format, {
      projectId: projectId.value,
      sessionId: sessionId.value
    })
    
    const formatNames = {
      pdf: 'PDF',
      docx: 'Word document',
      txt: 'text file',
      copy: 'clipboard'
    }
    
    const message = format === 'copy' 
      ? 'Report copied to clipboard!'
      : `Report exported as ${formatNames[format]}!`
    
    showSuccessMessage(message)
  } catch (err) {
    error.value = `Failed to export report: ${err.message}`
  } finally {
    isExporting.value = false
  }
}

/*const handleReportTypeChange = () => {
  // If report exists and type changed, show option to regenerate
  if (riskReport.value && riskReport.value.reportType !== selectedReportType.value) {
    if (confirm('Report type changed. Do you want to regenerate the report with the new type?')) {
      regenerateReport()
    } else {
      // Revert selection
      selectedReportType.value = riskReport.value.reportType
    }
  }
}

const verifyCitations = async () => {
  isLoading.value = true
  error.value = null
  
  try {
    if (!sessionId.value) {
      throw new Error('Session ID is required for citation verification')
    }
    
    await riskReportStore.verifyCitations(projectId.value, sessionId.value)
    showSuccessMessage('Citations verified successfully!')
  } catch (err) {
    error.value = err.message || 'Failed to verify citations'
  } finally {
    isLoading.value = false
  }
} */

const goToAnalysis = () => {
  router.push({
    path: '/projects/analyze',
    query: {
      projectId: projectId.value,
      sessionId: sessionId.value
    }
  })
}

const clearError = () => {
  error.value = null
}

const showSuccessMessage = (message) => {
  successMessage.value = message
  setTimeout(() => {
    successMessage.value = null
  }, 5000)
}

// Click outside handler for export menu
const handleClickOutside = (event) => {
  const exportMenu = event.target.closest('.relative')
  if (!exportMenu) {
    showExportMenu.value = false
  }
}

// Watch for report changes to update selected type
watch(() => riskReport.value, (newReport) => {
  if (newReport && newReport.reportType) {
    selectedReportType.value = newReport.reportType
  }
}, { immediate: true })

// Lifecycle
onMounted(async () => {
  // Close export menu on click outside
  document.addEventListener('click', handleClickOutside)
  
  if (sessionId.value) {
    // Check report status first
    try {
      await riskReportStore.checkReportStatus(projectId.value, sessionId.value)
      
      // If report exists, fetch it
      if (riskReportStore.reportStatus?.exists) {
        try {
          await riskReportStore.getRiskReport(projectId.value, sessionId.value)
        } catch (err) {
          // If fetch fails, clear the status and show generate option
          riskReportStore.reportStatus = null
          if (!err.message?.includes('404') && !err.message?.includes('not found')) {
            error.value = err.message || 'Failed to load existing risk report'
          }
        }
      }
    } catch (err) {
      // Status check failed, that's ok - user can generate report
      if (!err.message?.includes('404') && !err.message?.includes('not found')) {
        console.warn('Failed to check report status:', err.message)
      }
    }
  }
})

// Click outside handler for agent panel
const handleClickOutsideAgent = (event) => {
  if (agentPanelRef.value && !agentPanelRef.value.contains(event.target) && !event.target.closest('button[class*="fixed bottom-6 right-6"]')) {
    isAgentOpen.value = false
  }
}

// Watch agent open state to add/remove click listener
watch(isAgentOpen, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      document.addEventListener('click', handleClickOutsideAgent)
    }, 100)
  } else {
    document.removeEventListener('click', handleClickOutsideAgent)
  }
})

// Cleanup
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('click', handleClickOutsideAgent)
})
</script>